import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { FaFileAlt, FaWhatsapp } from "react-icons/fa";

import useCampaigns from "../../services/Whatsapp/useCampaigns";
import useTemplates from "../../services/Whatsapp/useTemplates";
import useInsights from "../../services/Whatsapp/useInsights";
import NoIndex from "../../components/NoIndex";
import useWhatsappNumbers from "../../services/Whatsapp/useWhatsappNumbers";
import { RefreshCcw } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const Insights = () => {
const { getCampaigns } = useCampaigns();
const { fetchVendorTemplates } = useTemplates();

const [campaigns, setCampaigns] = useState([]);
const [templates, setTemplates] = useState([]);

  const { whatsappNumbers } = useWhatsappNumbers();
  const { insights } = useInsights();

const getCampaignCacheKey = (phoneNumberId) =>
  `whatsapp_campaigns_cache_${phoneNumberId}`;

const getTemplateCacheKey = (phoneNumberId) =>
  `whatsapp_templates_cache_${phoneNumberId}`;

  const [loading, setLoading] = useState(true);

  const chartData = {
    labels: insights?.chartData?.map((i) => i.date) || [],
    datasets: [
      {
        label: "Sent",
        data: insights?.chartData?.map((i) => Number(i.messagesSent)) || [],
        borderColor: "#ef4444",
        tension: 0.4,
      },
      {
        label: "Delivered",
        data:
          insights?.chartData?.map((i) => Number(i.messagesDelivered)) || [],
        borderColor: "#8b5cf6",
        tension: 0.4,
      },
      {
        label: "Read",
        data: insights?.chartData?.map((i) => Number(i.messagesRead)) || [],
        borderColor: "#3b82f6",
        tension: 0.4,
      },
      {
        label: "Replies",
        data: insights?.chartData?.map((i) => Number(i.replies)) || [],
        borderColor: "#000000",
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
  };

  const LegendItem = ({ color, label }) => (
    <div className="flex items-center gap-2">
      <span
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: color }}
      ></span>
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  );

const handleRefresh = async () => {
  try {
    setLoading(true);

    const phoneNumberId = whatsappNumbers?.[0]?.phone_no_id;

    if (!phoneNumberId) {
      setCampaigns([]);
      setTemplates([]);
      return;
    }

    const [campaignList, templateList] = await Promise.all([
      getCampaigns(phoneNumberId),
      fetchVendorTemplates(phoneNumberId),
    ]);

    const finalCampaigns = Array.isArray(campaignList) ? campaignList : [];
    const finalTemplates = Array.isArray(templateList) ? templateList : [];

    setCampaigns(finalCampaigns);
    setTemplates(finalTemplates);

    sessionStorage.setItem(
      getCampaignCacheKey(phoneNumberId),
      JSON.stringify(finalCampaigns)
    );

    sessionStorage.setItem(
      getTemplateCacheKey(phoneNumberId),
      JSON.stringify(finalTemplates)
    );
  } catch (err) {
    console.error(err);
    setCampaigns([]);
    setTemplates([]);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  const load = async () => {
    try {
      setLoading(true);

      const phoneNumberId = whatsappNumbers?.[0]?.phone_no_id;

      if (!phoneNumberId) {
        setCampaigns([]);
        setTemplates([]);
        return;
      }

      const cachedCampaigns = sessionStorage.getItem(
        getCampaignCacheKey(phoneNumberId)
      );

      const cachedTemplates = sessionStorage.getItem(
        getTemplateCacheKey(phoneNumberId)
      );

      if (cachedCampaigns && cachedTemplates) {
        const parsedCampaigns = JSON.parse(cachedCampaigns);
        const parsedTemplates = JSON.parse(cachedTemplates);

        setCampaigns(Array.isArray(parsedCampaigns) ? parsedCampaigns : []);
        setTemplates(Array.isArray(parsedTemplates) ? parsedTemplates : []);

        return;
      }

      const [campaignList, templateList] = await Promise.all([
        getCampaigns(phoneNumberId),
        fetchVendorTemplates(phoneNumberId),
      ]);

      const finalCampaigns = Array.isArray(campaignList) ? campaignList : [];
      const finalTemplates = Array.isArray(templateList) ? templateList : [];

      setCampaigns(finalCampaigns);
      setTemplates(finalTemplates);

      sessionStorage.setItem(
        getCampaignCacheKey(phoneNumberId),
        JSON.stringify(finalCampaigns)
      );

      sessionStorage.setItem(
        getTemplateCacheKey(phoneNumberId),
        JSON.stringify(finalTemplates)
      );
    } catch (err) {
      console.error(err);
      setCampaigns([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  load();
}, [whatsappNumbers, getCampaigns, fetchVendorTemplates]);

  return (
    <>
      <NoIndex />
      <div className="p-6 h-full flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-30 pb-6">
  <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold">WhatsApp Insights</h2>

          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 border rounded-md text-white bg-green-400"
          >
            <RefreshCcw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
  {loading ? (
          <div className="flex items-center justify-center h-[70vh]">
            <div className="text-center">
                <LoadingScreen text="Updating ..." />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 montserrat-font-1">
            {/* LEFT PANEL */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">WhatsApp Dashboard</h3>
                <p className="text-gray-500 text-sm montserrat-font-2">
                  Campaign insights including performance & cost metrics.
                </p>
              </div>

              {/* Cost Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6 border p-6 rounded-lg">
                <div className="bg-gray-100 p-4 rounded-lg border">
                  <p className="text-sm text-gray-500 ">Amount Spent</p>
                  <h3 className="text-xl font-semibold">
                    {insights?.amountSpent}
                  </h3>
                </div>

                <div className="bg-gray-100 p-4 rounded-lg border">
                  <p className="text-sm text-gray-500">Cost per Delivered</p>
                  <h3 className="text-xl font-semibold">
                    {insights?.costPerMessageDelivered}
                  </h3>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Stat label="Sent" value={insights?.messagesSent} />
                <Stat label="Delivered" value={insights?.messagesDelivered} />
                <Stat label="Read" value={insights?.messagesRead} />
                <Stat label="Replies" value={insights?.replies} />
              </div>

              {/* Chart */}
              <div className="h-80">
                <Line data={chartData} options={options} />
              </div>
              <div className="flex justify-center gap-8 mt-6 flex-wrap">
                <LegendItem color="#ef4444" label="Sent" />
                <LegendItem color="#8b5cf6" label="Delivered" />
                <LegendItem color="#3b82f6" label="Read" />
                <LegendItem color="#000000" label="Replies" />
              </div>
            </div>

            {/* RIGHT PANEL - Full Height matching Left Panel */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full overflow-hidden">
              <div className="mb-4 shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">
                  Campaign & Template Logs
                </h3>
              </div>

              {/* Summary Cards - Shrink-0 taaki ye fix rahein */}
              <div className="grid grid-cols-2 gap-4 mb-4 shrink-0">
                <SummaryCard
                  icon={<FaFileAlt />}
                  title="Total Templates"
                  value={templates?.length}
                />
                <SummaryCard
                  icon={<FaWhatsapp />}
                  title="Total Campaigns"
                  value={campaigns?.length}
                />
              </div>

              {/* Tables Container - flex-grow se ye bachi hui puri height occupy kar lega */}
              <div className="flex-grow flex flex-col gap-4 overflow-hidden min-h-0">
                {/* Table 1 - Templates (Takes 50% of remaining space) */}
                <div className="flex-1 min-h-0">
                  <DataTable
                    title="Templates"
                    data={templates}
                    columns={["name", "status", "category"]}
                  />
                </div>

                {/* Table 2 - Campaigns (Takes 50% of remaining space) */}
                <div className="flex-1 min-h-0">
                  <DataTable
                    title="Campaigns"
                    data={campaigns}
                    columns={["campaign_name", "status", "audience"]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-500">{label}</p>
    <h4 className="text-lg font-semibold">{value}</h4>
  </div>
);

const SummaryCard = ({ icon, title, value }) => (
  <div className="bg-gray-100 p-4 rounded-lg flex items-center gap-3">
    <div className="text-blue-600 text-xl">{icon}</div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="text-xl font-semibold">{value}</h3>
    </div>
  </div>
);

const DataTable = ({ title, data = [], columns }) => (
  <div className="border rounded-lg overflow-hidden">
    <div className="bg-gray-50 px-4 py-2 font-semibold">{title}</div>
    <div className="max-h-64 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-100 sticky top-0">
          <tr>
            {columns.map((col) => (
              <th key={col} className="p-2 text-left capitalize">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((item, i) => (
            <tr key={i} className="border-t">
              {columns.map((col) => (
                <td key={col} className="p-2">
                  {col === "status" ? (
                    (() => {
                      const statusValue = item[col]?.toLowerCase();

                      if (statusValue === "n") {
                        return (
                          <span className="bg-yellow-500 text-white text-xs px-3 py-1 rounded-full">
                            Stopped
                          </span>
                        );
                      }

                      if (statusValue === "approved") {
                        return (
                          <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full">
                            Approved
                          </span>
                        );
                      }

                      return item[col];
                    })()
                  ) : col === "audience" ? (
                    <span className=" text-gray-900 text-sm px-3 py-1 rounded-full">
                      {item.members?.length || 0}
                    </span>
                  ) : (
                    item[col]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default Insights;
